import { cn } from "@/lib/utils";
import {
    ComponentProps,
    DetailedHTMLProps,
    InputHTMLAttributes,
    useEffect,
    useRef,
    useState
} from "react";

import { IconBox } from "../IconBox";
import { BaseFormBox, BaseFormBoxProps } from "./BaseFormBox";

type TextFieldProps = Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    "size"
> &
    BaseFormBoxProps & {
        startItem?: React.ReactNode;
        endItem?: React.ReactNode;
        hideClearButton?: boolean;
    };

export function TextField({
    style,
    className,
    size,
    interaction,
    variant,
    disabled,
    error,
    startItem,
    endItem,
    hideClearButton,
    value,
    onChange,
    defaultValue,
    ...props
}: TextFieldProps) {
    const localRef = useRef<HTMLInputElement>(null);
    const [hasValue, setHasValue] = useState(() => {
        // Initialize state based on value or defaultValue
        if (value !== undefined) {
            return String(value).length > 0;
        }
        if (defaultValue !== undefined) {
            return String(defaultValue).length > 0;
        }
        return false;
    });

    // Sync with controlled value prop
    useEffect(() => {
        if (value !== undefined) {
            setHasValue(String(value).length > 0);
        } else if (localRef.current) {
            // For uncontrolled inputs, check the actual DOM value
            setHasValue(localRef.current.value.length > 0);
        }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setHasValue(e.target.value.length > 0);
        onChange?.(e);
    };

    const handleClear = () => {
        const target = localRef.current;
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            "value"
        )?.set;

        if (target && nativeInputValueSetter) {
            nativeInputValueSetter.call(target, "");
            target.dispatchEvent(new Event("input", { bubbles: true }));
            setHasValue(false);
        }
    };

    return (
        <BaseFormBox
            className={cn("flex items-center gap-1", className)}
            style={style}
            size={size}
            variant={variant}
            interaction={interaction}
            disabled={disabled}
            error={error}
        >
            {startItem}
            <input
                ref={localRef}
                className={cn(
                    "w-full flex-1 border-none bg-transparent outline-none",
                    "text-[14px] font-normal leading-[22px] text-text-primary",
                    "placeholder:text-text-disabled",
                    // Number type - hide arrows
                    "[type=number]:[-moz-appearance:textfield]",
                    "[type=number]:[-webkit-appearance:none]",
                    "[type=number]:[::-webkit-inner-spin-button]:appearance-none",
                    "[type=number]:[::-webkit-outer-spin-button]:appearance-none"
                )}
                disabled={disabled}
                placeholder={props.placeholder || ""}
                value={value}
                defaultValue={defaultValue}
                onChange={handleChange}
                {...props}
            />
            {!disabled && !hideClearButton && hasValue && (
                <IconBox
                    className="cursor-pointer text-grey-400"
                    name="Close"
                    size="medium"
                    data-close-icon
                    onClick={handleClear}
                />
            )}
            {endItem}
        </BaseFormBox>
    );
}

const Icon = (props: ComponentProps<typeof IconBox>) => {
    return <IconBox size="large" {...props} />;
};

TextField.Icon = Icon;
